import { validateWellnessSubmission, type WellnessAnswer } from './validation.js';

export const uacIdentityFields = ['alienNumberPrimary', 'uacFirstName', 'uacLastName'] as const;
export type UacIdentityField = typeof uacIdentityFields[number];
export interface WellnessIdentityPrefill {
  caseNumber: string;
  source: 'case_import' | 'saved_form' | 'unavailable' | 'none';
  values: Partial<Record<UacIdentityField, string>>;
}

/** Accept only explicit identity fields. Never split display names or invent digits. */
export function validatedUacIdentity(input: Record<string, unknown>): Partial<Record<UacIdentityField, string>> {
  const values: Partial<Record<UacIdentityField, string>> = {};
  for (const field of uacIdentityFields) {
    const raw = input[field];
    if (typeof raw !== 'string' || !raw.trim() || raw.includes('\u0000')) continue;
    const value = raw.normalize('NFKC').trim().replace(/\s+/gu, ' ');
    if (field === 'alienNumberPrimary') {
      if (/^\d{9}$/u.test(value)) values[field] = value;
    } else if (value.length <= 100 && !validateWellnessSubmission({ [field]: value }).errors[field]) {
      values[field] = value;
    }
  }
  return values;
}

/** Fill blanks once on opening. Existing answers always take precedence. */
export function applyUacIdentityPrefill(answers: WellnessAnswer, prefill?: WellnessIdentityPrefill) {
  const filled: UacIdentityField[] = [];
  if (!prefill || !/^14-\d{5}$/u.test(prefill.caseNumber) || answers.nonPliCase !== prefill.caseNumber
      || !['case_import', 'saved_form'].includes(prefill.source)) return { answers, filled, conflict: false };
  const values = validatedUacIdentity(prefill.values);
  const conflict = uacIdentityFields.some((field) => answers[field]?.trim() && values[field]
    && answers[field].normalize('NFKC').trim().replace(/\s+/gu, ' ').toLocaleLowerCase() !== values[field]!.toLocaleLowerCase());
  if (conflict) return { answers, filled, conflict: true };
  const next = { ...answers };
  for (const field of uacIdentityFields) {
    if (!next[field]?.trim() && values[field]) { next[field] = values[field]!; filled.push(field); }
  }
  next.uacAlienNumber = next.alienNumberPrimary ?? '';
  return { answers: next, filled, conflict: false };
}

/** Source headings must name the UAC explicitly; FIRST_NAME is the case officer. */
export function identityFromImport(header: unknown, row: unknown, caseNumber: string): WellnessIdentityPrefill {
  const empty: WellnessIdentityPrefill = { caseNumber, source: 'none', values: {} };
  if (!Array.isArray(header) || !Array.isArray(row) || header.length > 128) return empty;
  const cells = new Map<string, string>();
  for (let index = 0; index < header.length; index++) {
    if (typeof header[index] !== 'string') continue;
    const key = header[index].normalize('NFKC').trim().toUpperCase().replace(/[\s-]+/gu, '_');
    if (!['CASE_ID', 'A_NUMBER', 'UAC_A_NUMBER', 'UAC_FIRST_NAME', 'UAC_LAST_NAME'].includes(key)) continue;
    if (cells.has(key)) return empty;
    cells.set(key, typeof row[index] === 'string' ? row[index].trim() : '');
  }
  if (cells.has('CASE_ID') && cells.get('CASE_ID') !== caseNumber) return empty;
  const primary = cells.get('A_NUMBER');
  const repeated = cells.get('UAC_A_NUMBER');
  if (primary && repeated && primary !== repeated) return empty;
  const values = validatedUacIdentity({ alienNumberPrimary: primary || repeated,
    uacFirstName: cells.get('UAC_FIRST_NAME'), uacLastName: cells.get('UAC_LAST_NAME') });
  return { caseNumber, source: Object.keys(values).length ? 'case_import' : 'none', values };
}
